import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Share2,
  Search,
  MapPin,
  Stethoscope,
  Loader2,
  X,
  Users2,
  Plus,
} from "lucide-react";

/**
 * Fase 5.9: card para que un paciente comparta su expediente con médicos
 * del directorio. Muestra la lista de accesos otorgados y un buscador para
 * seleccionar un médico visible del directorio.
 */
export default function CompartirExpedienteCard() {
  const [accesos, setAccesos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null); // acceso object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expediente/accesos");
      setAccesos(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const revoke = async () => {
    if (!confirmRevoke) return;
    try {
      await api.delete(`/expediente/accesos/${confirmRevoke.id}`);
      toast.success("Acceso revocado.");
      setConfirmRevoke(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const activos = accesos.filter((a) => a.estado === "activo");

  return (
    <section
      className="mt-6 rounded-xl border border-border bg-white p-6"
      data-testid="compartir-expediente-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#0F2942] text-white grid place-items-center shrink-0">
            <Share2 size={18} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#2563EB] font-semibold">
              Compartir expediente
            </div>
            <h3 className="font-display text-lg font-semibold text-[#0F2942] tracking-tight">
              Autoriza a un especialista a ver tu expediente
            </h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-xl leading-relaxed">
              Elige un médico del directorio para compartir tu historia clínica, laboratorios
              y consultas IA. Puedes revocar el acceso en cualquier momento — la lectura del
              médico se corta inmediatamente.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
          onClick={() => setAddOpen(true)}
          data-testid="compartir-open-btn"
        >
          <Plus size={14} /> Compartir con médico
        </Button>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando accesos…
          </div>
        ) : activos.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground"
            data-testid="compartir-empty"
          >
            Aún no compartes tu expediente con ningún médico.
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="compartir-list">
            {activos.map((a) => (
              <li
                key={a.id}
                className="rounded-lg border border-border bg-muted/20 p-3 flex items-start justify-between gap-3"
                data-testid={`acceso-${a.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#0F2942] truncate">
                    {a.medico_nombre}
                  </div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Stethoscope size={11} /> {a.medico_especialidad || "Especialidad"}
                  </div>
                  {(a.medico_ciudad || a.medico_estado) && (
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <MapPin size={11} /> {a.medico_ciudad}
                      {a.medico_ciudad && a.medico_estado ? ", " : ""}
                      {a.medico_estado}
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-700 hover:bg-red-50"
                  onClick={() => setConfirmRevoke(a)}
                  data-testid={`acceso-revoke-${a.id}`}
                >
                  <X size={14} /> Revocar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AddAccesoDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        currentIds={activos.map((a) => a.medico_id)}
        onGranted={() => {
          setAddOpen(false);
          load();
        }}
      />

      <Dialog open={!!confirmRevoke} onOpenChange={(o) => !o && setConfirmRevoke(null)}>
        <DialogContent data-testid="acceso-revoke-dialog">
          <DialogHeader>
            <DialogTitle>¿Revocar el acceso?</DialogTitle>
            <DialogDescription>
              {confirmRevoke && (
                <>
                  <b>{confirmRevoke.medico_nombre}</b> dejará de ver tu expediente
                  inmediatamente. Podrás volver a compartir en el futuro si lo deseas.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRevoke(null)}>
              Cancelar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={revoke}
              data-testid="acceso-revoke-confirm"
            >
              Revocar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function AddAccesoDialog({ open, onClose, currentIds, onGranted }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [granting, setGranting] = useState(null);

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/directorio", {
          params: { q: q || undefined, per_page: 8 },
        });
        setResults(data.items || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const grant = async (medico) => {
    setGranting(medico.id);
    try {
      await api.post("/expediente/compartir", { medico_id: medico.id });
      toast.success(`Compartiste tu expediente con ${medico.nombre_completo}.`);
      onGranted();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGranting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="compartir-search-dialog">
        <DialogHeader>
          <DialogTitle>Elige un médico del directorio</DialogTitle>
          <DialogDescription>
            Solo aparecen los médicos visibles en el Directorio de Especialistas.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-3 text-muted-foreground" size={14} />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, especialidad o ciudad…"
            className="pl-9"
            data-testid="compartir-search-input"
            autoFocus
          />
        </div>
        <div className="max-h-80 overflow-y-auto -mx-1 px-1 space-y-2">
          {searching ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Buscando…
            </div>
          ) : results.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              Ningún médico coincide con tu búsqueda.
            </div>
          ) : (
            results.map((m) => {
              const already = currentIds.includes(m.id);
              return (
                <div
                  key={m.id}
                  className="rounded-lg border border-border bg-white px-3 py-2 flex items-center gap-3"
                  data-testid={`compartir-result-${m.id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-[#0F2942]/5 text-[#0F2942] grid place-items-center font-semibold text-xs shrink-0">
                    {m.iniciales}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#0F2942] truncate">
                      {m.nombre_completo}
                    </div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                      <span className="flex items-center gap-1">
                        <Stethoscope size={11} /> {m.especialidad}
                      </span>
                      {m.ciudad && (
                        <span className="flex items-center gap-1">
                          <MapPin size={11} /> {m.ciudad}
                          {m.estado ? `, ${m.estado}` : ""}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    disabled={already || granting === m.id}
                    onClick={() => grant(m)}
                    className={
                      already
                        ? ""
                        : "bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
                    }
                    variant={already ? "outline" : "default"}
                    data-testid={`compartir-grant-${m.id}`}
                  >
                    {granting === m.id ? (
                      <>
                        <Loader2 size={12} className="animate-spin" /> …
                      </>
                    ) : already ? (
                      <>
                        <Users2 size={12} /> Ya compartido
                      </>
                    ) : (
                      <>
                        <Share2 size={12} /> Compartir
                      </>
                    )}
                  </Button>
                </div>
              );
            })
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
